import type {
  IErrorApiResponse,
  IPaginationParams,
  IPaginationResponse,
  ISuccessApiResponse,
} from '@wyz/types';
import {
  OverridableQuery,
  restrictApi,
  serialize,
  useQuery,
  UseQueryOptions,
} from '@wyz/utils';
import { useCallback } from 'react';
import { useApi, useConfig } from '~/common/context';
import {
  IAnonymizedSupplierPlatform,
  ISupplierPlatform,
  ISupplierPlatformFilterQuery,
} from '~/connect/types';
import { CommonQueryKeys } from '~/common/api';
import { ConnectQueryKeys } from '~/connect/api';

type Options = {
  paginationFetcher?: {
    enabled?: boolean;
  };
  list?: OverridableQuery<
    UseQueryOptions<IPaginationResponse<ISupplierPlatform>>
  > & {
    args?: {
      supplierTypeIds?: Array<number>;
      supplierWyzApiIds?: Array<number>;
    };
  };
  anonymized?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<Array<IAnonymizedSupplierPlatform>>,
      IErrorApiResponse
    >
  >;
};

export const useManageSupplierPlatforms = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { tenantCode } = useConfig();
  const { callCoreApi, tenantsDomainBaseUrl, suppliersDomainBaseUrl } =
    useApi();

  const paginationFetcher = useCallback(
    (query: IPaginationParams & ISupplierPlatformFilterQuery) => {
      return callCoreApi<IPaginationResponse<ISupplierPlatform>>(
        `${tenantsDomainBaseUrl}/tenants/${tenantCode}/supplier-platforms?${serialize(
          query,
        )}`,
      );
    },
    [callCoreApi, tenantsDomainBaseUrl, tenantCode],
  );
  const listQuery = useQuery({
    enabled: false,
    ...options.list,
    queryKey: CommonQueryKeys.supplierPlatforms.paginate(options.list?.args),
    queryFn: () =>
      callCoreApi<IPaginationResponse<ISupplierPlatform>>(
        `${suppliersDomainBaseUrl}/tenants/${tenantCode}/supplier-platforms?${serialize(
          options.list?.args ?? {},
        )}`,
      ),
    refetchOnWindowFocus: false,
  });

  const anonymizedQuery = useQuery({
    queryKey: ConnectQueryKeys.suppliers.base(),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<Array<IAnonymizedSupplierPlatform>>>(
        `${suppliersDomainBaseUrl}/tenants/${tenantCode}/supplier-platforms/anonymized`,
      ),
    enabled: false,
    refetchOnWindowFocus: false,
    ...options.anonymized,
  });

  return restrictApi(options, {
    paginationFetcher,
    list: listQuery,
    anonymized: anonymizedQuery,
  });
};
